import * as React from 'react';

import { clsx } from '@freelancelabs/utils';

export interface SelectNativeProps<T> extends React.SelectHTMLAttributes<HTMLSelectElement> {
    onValue?: (value: T) => void;
    options: { value: T; label: string }[];
    error?: React.ReactNode | boolean;
    selectClassName?: string;
}

type OptionValue = string | number | readonly string[] | undefined;

const SelectBase = <T extends OptionValue>(
    {
        className: classNameProp,
        selectClassName,
        onChange,
        value,
        onValue,
        options,
        disabled,
        error,
        ...rest
    }: SelectNativeProps<T>,
    ref: React.Ref<HTMLSelectElement>
) => {
    const [focused, setFocused] = React.useState(false);
    return (
        <div
            className={clsx(
                'input-wrapper',
                Boolean(error) && 'error',
                disabled && 'disabled',
                focused && 'is-focus',
                classNameProp
            )}
        >
            <select
                value={value}
                // `disabled` is destructured out of the props to style the wrapper, so
                // it has to be put back on the control: without this line the select
                // looked disabled and stayed fully operable: the worst of the two,
                // since a screen that greys a filter is promising it does nothing.
                disabled={disabled}
                onChange={(e) => {
                    onValue?.(e.target.value as T);
                    onChange?.(e);
                }}
                ref={ref}
                className={clsx('input select-native', selectClassName)}
                onFocus={() => {
                    setFocused(true);
                }}
                onBlur={() => {
                    setFocused(false);
                }}
                {...rest}
            >
                {options.map((option, index) => (
                    <option key={index} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const SelectNative = React.forwardRef(SelectBase) as <T extends OptionValue>(
    p: SelectNativeProps<T> & { ref?: React.Ref<HTMLSelectElement> }
) => React.ReactElement;

export default SelectNative;
